import React from 'react';

import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { fmt_count } from '../format';
import {
  CLOSE_DISABLED_TEXT,
  DECISIONS_EMPTY_TEXT,
  DECISIONS_LOAD_FAILED_TEXT,
  READINESS_LOAD_FAILED_TEXT,
  TIERS_EMPTY_TEXT,
  TIERS_LOAD_FAILED_TEXT
} from './adapters';

/*
 * the month summary: the readiness header ("N errors, M must-review open → not ready to close")
 * and the seven tiers with teachers / sessions / ₹ at stake.
 */
const TierSummary = ({
  summary,
  load_failed = false,
  month_label,
  on_select_tier,
  on_close_month,
  on_reopen,
  on_see_decisions,
  run_busy_text = '',
  settle_note = '',
  on_refresh,
  on_retry
}) => {
  const { readiness, tiers } = summary;

  const columns = [
    {
      key: 'label',
      label: 'Tier',
      hideInCard: true,
      render: (t) => (
        <button type="button" className="tp-link" onClick={() => on_select_tier(t.tier)}>
          <div>{t.label}</div>
          <div className="tp-card-field-label">{t.status_text}</div>
        </button>
      )
    },
    {
      key: 'blocks_close',
      label: 'Blocks close',
      render: (t) => (t.blocks_close ? <StatusPill tone="red">Blocks close</StatusPill> : '—')
    },
    {
      key: 'open_count',
      label: 'Open',
      align: 'right',
      render: (t) => fmt_count(t.open_count)
    },
    // decided work sits beside the open count, not only in the issue list.
    {
      key: 'acknowledged_count',
      label: 'Acknowledged',
      align: 'right',
      render: (t) => fmt_count(t.acknowledged_count)
    },
    {
      key: 'resolved_count',
      label: 'Resolved',
      align: 'right',
      render: (t) => fmt_count(t.resolved_count)
    },
    {
      key: 'teachers',
      label: 'Teachers',
      align: 'right',
      render: (t) => fmt_count(t.teachers)
    },
    {
      key: 'sessions',
      label: 'Sessions',
      align: 'right',
      render: (t) => fmt_count(t.sessions)
    },
    {
      key: 'amount_at_stake',
      label: '₹ at stake',
      align: 'right',
      render: (t) => <span className="tp-strong">{t.amount_text}</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      hideInCard: true,
      render: (t) =>
        t.can_drill ? (
          <button type="button" className="tp-link" onClick={() => on_select_tier(t.tier)}>
            View issues
          </button>
        ) : (
          '—'
        )
    }
  ];

  const decision_columns = [
    {
      key: 'action',
      label: 'Decision',
      hideInCard: true,
      render: (d) => <StatusPill tone={d.action_tone}>{d.action_label}</StatusPill>
    },
    {
      key: 'label',
      label: 'Issue',
      render: (d) => (
        <div>
          <div>{d.label}</div>
          <div className="tp-card-field-label">
            <StatusPill tone={d.tier_tone}>{d.tier_label}</StatusPill>{' '}
            <span className="tp-mono">{d.issue_code}</span>
          </div>
        </div>
      )
    },
    {
      key: 'teacher_name',
      label: 'Teacher',
      render: (d) => (
        <div>
          <div>{d.teacher_name}</div>
          <div className="tp-card-field-label tp-mono" title={d.teacher_uuid}>
            {d.teacher_short}
          </div>
        </div>
      )
    },
    {
      key: 'who',
      label: 'By',
      render: (d) => (
        <div>
          <div title={d.who_uuid || undefined}>{d.who}</div>
          <div className="tp-card-field-label">{d.when_text}</div>
        </div>
      )
    },
    {
      key: 'note',
      label: 'Note',
      render: (d) => d.note || '—'
    }
  ];

  return (
    <div>
      {load_failed ? (
        <LoadFailed text={READINESS_LOAD_FAILED_TEXT} on_retry={on_retry} />
      ) : (
        <div className={`tp-banner tp-banner-${readiness.tone}`}>
          <div className="tp-na-headline">
            Month readiness · {month_label} — {readiness.headline}
          </div>
          {readiness.teachers_text ? <div>{readiness.teachers_text}</div> : null}
          {readiness.override_text ? <div>{readiness.override_text}</div> : null}
        </div>
      )}

      {summary.close_enabled ? null : (
        <div className="tp-banner tp-banner-warn" role="status">
          {CLOSE_DISABLED_TEXT}
        </div>
      )}

      {settle_note ? (
        <div className="tp-banner tp-banner-info" role="status">
          {settle_note}{' '}
          <button type="button" className="tp-link" onClick={on_refresh}>
            Refresh
          </button>
        </div>
      ) : null}

      {/* one active run per month; off because of OTHER state, so natively disabled. */}
      <div className="tp-frow">
        <button
          type="button"
          className="tp-btn"
          onClick={on_close_month}
          disabled={!!run_busy_text}
        >
          Close month…
        </button>
        <button type="button" className="tp-btn-out" onClick={on_reopen} disabled={!!run_busy_text}>
          Reopen a teacher-month…
        </button>
        {run_busy_text ? <span className="tp-card-hd-sub">{run_busy_text}</span> : null}
        <div className="tp-frow-right">
          <span className="tp-card-hd-sub">
            {summary.generated_at ? `Data as of ${summary.generated_at} IST` : ''}
          </span>
        </div>
      </div>

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-title">
            Issues by tier · {month_label}{' '}
            {load_failed ? null : (
              <span className="tp-card-hd-sub">
                {fmt_count(summary.total_outstanding)} open or acknowledged
              </span>
            )}
          </span>
          <span className="tp-card-hd-sub">Click a tier to see exactly those issues</span>
        </div>
        <DataTable
          columns={columns}
          rows={tiers}
          rowKey={(t) => t.key}
          emptyText={load_failed ? TIERS_LOAD_FAILED_TEXT : TIERS_EMPTY_TEXT}
          cardTitle={(t) => (
            <button
              type="button"
              className="tp-link"
              style={{ fontSize: 14 }}
              onClick={() => on_select_tier(t.tier)}
            >
              {t.label}
            </button>
          )}
        />
      </div>

      <section className="tp-card" aria-label="Recent Ops decisions">
        <div className="tp-card-hd">
          <span className="tp-card-hd-title">
            Recent Ops decisions · {month_label}{' '}
            <span className="tp-card-hd-sub">{summary.decisions_count_text}</span>
          </span>
          {summary.can_see_all_decisions ? (
            <button type="button" className="tp-link" onClick={on_see_decisions}>
              See all {fmt_count(summary.decisions_total)} Ops decisions
            </button>
          ) : null}
        </div>
        <DataTable
          columns={decision_columns}
          rows={summary.decisions}
          rowKey={(d) => d.key}
          emptyText={load_failed ? DECISIONS_LOAD_FAILED_TEXT : DECISIONS_EMPTY_TEXT}
          cardTitle={(d) => (
            <span>
              <StatusPill tone={d.action_tone}>{d.action_label}</StatusPill>
              {d.by_text ? <span title={d.who_uuid || undefined}> {d.by_text}</span> : null}
            </span>
          )}
        />
      </section>
    </div>
  );
};

export default TierSummary;
